import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { X, ArrowRight, Sparkles, Clock, Gift } from 'lucide-react';

const SESSION_KEY = 'varotrades_exit_intent_seen_v1';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Don't fire on these
const BLOCKED_PATHS = [
  '/admin', '/dashboard', '/settings', '/kyc', '/funds',
  '/wallet', '/register', '/login', '/vwallet', '/copy-trading',
  '/super-admin', '/crm', '/install',
];

// ----- Live countdown (10:00 minutes) -----
const Countdown = () => {
  const [s, setS] = useState(10 * 60);
  useEffect(() => {
    const t = setInterval(() => setS(prev => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return (
    <span className="font-mono tabular-nums text-flame-600 font-black">{mm}:{ss}</span>
  );
};

const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  useBodyScrollLock(open);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const firedRef = useRef(false);

  const t = (ar, en) => (isRTL ? ar : en);

  useEffect(() => {
    if (authLoading || isAuthenticated) return;
    if (BLOCKED_PATHS.some(p => location.pathname.startsWith(p))) return;

    // Per user request: re-show on every page reload (no session-storage guard).
    // The firedRef inside this component still prevents multiple triggers within the same page lifecycle.

    // Desktop only — mobile has no reliable "mouse leaves to close tab" gesture
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (isTouchDevice) return;

    // Arm after 3s of presence (avoid firing the instant the page loads)
    const armTimer = setTimeout(() => {
      const onMouseLeave = (e) => {
        if (firedRef.current) return;
        // Only top edge — left/right/bottom are accidental
        if (e.clientY < 10 && !e.relatedTarget) {
          firedRef.current = true;
          // Make sure PromoPopup is not already showing
          const promoOpen = document.querySelector('[data-testid="promo-popup"]');
          if (promoOpen) return;
          setOpen(true);
          try { trackEvent('ExitIntent_Shown', { path: location.pathname }); } catch {}
          document.removeEventListener('mouseleave', onMouseLeave);
        }
      };
      document.addEventListener('mouseleave', onMouseLeave);
      // Cleanup on unmount
      return () => document.removeEventListener('mouseleave', onMouseLeave);
    }, 8000);

    return () => clearTimeout(armTimer);
  }, [authLoading, isAuthenticated, location.pathname]);

  const close = (source = 'dismiss') => {
    try { trackEvent('ExitIntent_Dismissed', { source }); } catch {}
    setOpen(false);
  };

  const cta = () => {
    try { trackEvent('ExitIntent_CTA_Click', { content_name: 'extra_20_bonus' }); } catch {}
    try {
      localStorage.setItem('varotrades_bonus_code', 'EXTRA20');
      localStorage.setItem('varotrades_acquisition', JSON.stringify({
        utm_source: 'site',
        utm_medium: 'exit_intent',
        utm_campaign: 'extra_20',
      }));
    } catch {}
    setOpen(false);
    navigate('/register?utm_source=site&utm_medium=exit_intent&utm_campaign=extra_20');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1010] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="exit-intent-popup"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-ink-900/75 backdrop-blur-[8px]"
            onClick={() => close('backdrop')}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />

          {/* Card — compact, urgent */}
          <motion.div
            className="relative w-full max-w-[480px] rounded-3xl overflow-hidden bg-white"
            style={{
              boxShadow: '0 30px 80px -16px rgba(20,15,10,0.6), 0 0 0 1px rgba(255,255,255,0.08)',
              maxHeight: '70vh',
            }}
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ type: 'spring', damping: 18, stiffness: 240 }}
          >
            {/* Close */}
            <button
              onClick={() => close('close_button')}
              className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} z-30 w-8 h-8 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center text-ink-700 hover:text-ink-900`}
              aria-label="Close"
              data-testid="exit-intent-close"
            >
              <X className="w-3.5 h-3.5" />
            </button>

            {/* Top flame band with urgency icon */}
            <div className="relative px-6 pt-7 pb-5 text-center overflow-hidden"
              style={{
                background: 'radial-gradient(120% 80% at 50% 0%, #FF8A3D 0%, #FF5722 55%, #C13A12 100%)',
              }}>
              {/* Grain */}
              <div className="absolute inset-0 opacity-[0.10] mix-blend-overlay pointer-events-none"
                style={{
                  backgroundImage:
                    'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27160%27 height=%27160%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")',
                }} />

              {/* Real VAROTRADES emblem */}
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative z-10 flex justify-center mb-3"
              >
                <img
                  src={`${API_URL}/api/media/varotrade_official_emblem.png`}
                  alt="VAROTRADES"
                  className="h-8 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)] brightness-0 invert"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </motion.div>

              {/* Pulsing alert badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', damping: 12 }}
                className="relative z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/30 backdrop-blur-sm mb-2"
              >
                <motion.span
                  className="w-1.5 h-1.5 rounded-full bg-yellow-300"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
                <span className="text-[10px] font-black tracking-[0.18em] uppercase text-white">
                  {t('انتظر! · لا تغادر بعد', 'Wait! · Don\'t leave yet')}
                </span>
              </motion.div>

              {/* Hero headline */}
              <motion.h2
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.5 }}
                className="relative z-10 text-[26px] sm:text-[30px] font-black text-white leading-[1.1] tracking-tight"
              >
                {t('خذ معاك ', 'Take with you ')}
                <span className="text-yellow-200">{t('+20% إضافية', '+20% extra')}</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="relative z-10 text-white/95 text-[13px] mt-1.5 max-w-[340px] mx-auto leading-snug"
              >
                {t(
                  'بونص حصري على أول إيداع · يتوفّر فقط للزوار الذين سجّلوا الخروج',
                  'Exclusive first-deposit bonus · only for visitors about to leave'
                )}
              </motion.p>
            </div>

            {/* Body */}
            <div className="relative px-6 py-5">
              {/* Countdown */}
              <motion.div
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center justify-center gap-2 mb-4 px-3 py-2 rounded-lg bg-flame-50 border border-flame-100"
              >
                <Clock className="w-3.5 h-3.5 text-flame-600" />
                <span className="text-[11px] font-bold text-ink-700">
                  {t('هذا العرض ينتهي خلال', 'Offer expires in')}
                </span>
                <Countdown />
              </motion.div>

              {/* Mini bullets */}
              <motion.ul
                initial="hidden" animate="visible"
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.06, delayChildren: 0.55 } } }}
                className="space-y-1.5 mb-5"
              >
                {[
                  t('50% بونص ترحيبي + 20% إضافي من هذا العرض', '50% welcome bonus + 20% extra from this offer'),
                  t('بدون عمولة سحب · بدون رسوم خفيّة', 'No withdrawal fees · No hidden charges'),
                  t('تفعيل فوري بعد أول إيداع', 'Activates instantly on first deposit'),
                ].map((text, i) => (
                  <motion.li key={i}
                    variants={{ hidden: { opacity: 0, x: isRTL ? 8 : -8 }, visible: { opacity: 1, x: 0 } }}
                    className="flex items-start gap-2 text-[13px] text-ink-700"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-flame-500 mt-0.5 shrink-0" />
                    <span>{text}</span>
                  </motion.li>
                ))}
              </motion.ul>

              {/* CTA */}
              <motion.button
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, type: 'spring', damping: 18 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={cta}
                className="relative group w-full h-[54px] rounded-2xl text-white font-black text-[15px] tracking-tight overflow-hidden"
                style={{
                  background: 'linear-gradient(105deg, #FF5722 0%, #FF8A3D 100%)',
                  boxShadow: '0 14px 38px -10px rgba(255,87,34,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
                }}
                data-testid="exit-intent-cta"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  {t('فعّل البونص الإضافي 20%', 'Claim my extra 20% bonus')}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''} transition-transform group-hover:translate-x-0.5`} />
                </span>
                <motion.span
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.45) 50%, transparent 62%)' }}
                  animate={{ x: ['-110%', '110%'] }}
                  transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.2, ease: 'easeInOut' }}
                />
              </motion.button>

              <button
                onClick={() => close('no_thanks')}
                className="block w-full text-center text-[11px] text-ink-400 hover:text-ink-700 font-semibold mt-2.5 transition-colors"
                data-testid="exit-intent-no-thanks"
              >
                {t('لا أريد البونص، اخرج', 'I don\'t want the bonus, leave')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
